import React, {useEffect, useState} from "react";
import TextInput from "../../input/text/type/TextInput";
import FlexContainer from "../FlexContainer";
import SvgIcon from "../../display/SvgIcon";
import Text from "../../display/Text";
import ICONS from "../../../icons/iconMap";
import {buildStyles} from "../../buildStyles";
import {useTheme} from "../../../theme/ThemeProvider";

const useStyles = (theme) =>
  buildStyles({
    hoverIcon: {
      width: theme.spacing(4),
      height: theme.spacing(4),
    },
    editIcon: {
      width: theme.spacing(4),
      height: theme.spacing(4),
      color: theme.colors.textSecondary,
      display: "inline-flex !important",
      marginRight: theme.spacing(2),
      verticalAlign: "text-bottom",
      cursor: "pointer",
      transition: "opacity 0.15s",
    },
    actionsIcon: {
      width: theme.spacing(4),
      height: theme.spacing(4),
      cursor: "pointer",
    },
    textContainer: {
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
  });

export default function EditableFieldValue({
  value,
  onChange,
  emptyText = "None",
  error,
  customInput,
  onClose,
  controlledEdit = null,
  onEditChange = () => {},
}) {
  const {theme, isMobile} = useTheme();
  const classes = useStyles(theme);

  const [editing, setEditing] = useState(false);
  const [localValue, setLocalValue] = useState(value);
  const [showEditIcon, setShowEditIcon] = useState(false);

  useEffect(() => {
    if (controlledEdit !== null) {
      setEditing(controlledEdit);
      setShowEditIcon(controlledEdit);
    }
  }, [controlledEdit]);

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  const handleEdit = (isEditing) => {
    setEditing(isEditing);
    onEditChange(isEditing);
  };

  const handleSave = () => {
    onChange(localValue);
    setShowEditIcon(false);
    handleEdit(false);
  };

  const handleCancel = () => {
    setLocalValue(value);
    setShowEditIcon(false);
    handleEdit(false);
    if (onClose) {
      onClose();
    }
  };

  return editing ? (
    !!customInput ? (
      <FlexContainer alignItems="center" gap={theme.spacing(1)}>
        <SvgIcon
          className={classes.actionsIcon}
          component={ICONS["small-x"]}
          sx={{color: theme.colors.textSecondary}}
          onClick={handleCancel}
        />
        <SvgIcon
          className={classes.actionsIcon}
          component={ICONS.check}
          sx={{
            color: theme.colors.textSecondary,
            marginRight: theme.spacing(1),
          }}
          onClick={handleSave}
        />
        <FlexContainer
          flexDirection="column"
          sx={{
            borderRadius: theme.radius.sm,
            backgroundColor: theme.colors.gray100,
            padding: theme.spacing(1, 2),
            border: !!error ? `1px solid ${theme.colors.error}` : "none",
          }}
        >
          {customInput({value: localValue, onChange: setLocalValue})}
        </FlexContainer>
      </FlexContainer>
    ) : (
      <TextInput
        alignItems={"flex-end"}
        sx={{alignItems: "center"}}
        inputProps={{
          placeholder: localValue,
          value: localValue,
          onChange: (e) => setLocalValue(e.target.value),
          style: {
            backgroundColor: theme.colors.gray100,
            borderRadius: `${theme.radius.sm}px`,
            padding: theme.spacing(1, 2),
            textAlign: "end",
            ...theme.typography.textSM.medium,
          },
        }}
        startIcon={
          <FlexContainer
            alignItems="center"
            gap={theme.spacing(1)}
            sx={{marginRight: theme.spacing(2)}}
          >
            <SvgIcon
              className={classes.actionsIcon}
              component={ICONS["small-x"]}
              sx={{color: theme.colors.textSecondary}}
              onClick={handleCancel}
            />
            <SvgIcon
              className={classes.actionsIcon}
              component={ICONS.check}
              sx={{color: theme.colors.textSecondary}}
              onClick={handleSave}
            />
          </FlexContainer>
        }
      />
    )
  ) : (
    <FlexContainer
      alignItems="center"
      justifyContent="flex-start"
      sx={{overflow: "hidden", maxWidth: "100%"}}
      onMouseEnter={() => setShowEditIcon(true)}
      onMouseLeave={() => setShowEditIcon(false)}
    >
      <SvgIcon
        className={classes.editIcon}
        component={ICONS.edit}
        onClick={() => handleEdit(true)}
        sx={{opacity: showEditIcon || isMobile ? 1 : 0}}
      />
      <Text
        variant={!localValue ? "textXS" : "textSM"}
        weight="medium"
        color={!localValue ? "textTertiary" : "textPrimary"}
        className={classes.textContainer}
      >
        {localValue || emptyText}
      </Text>
    </FlexContainer>
  );
}
